import React, {useEffect, useState} from "react"
import moment from "moment"

import FormattedMessage from "@irati/utils/lang"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import mainTheme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import intercom from "@irati/utils/intercom"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import {ZuoraCurrentPlan, ZuoraProduct} from "@irati/types/ops"
import Loading from "@irati/ui/src/Loading"

import {BillingCard, BillingRenewal, BillingChip, BillingInfo} from "./styled"

interface Props {
  onCallback: () => any
}

const CurrentPlan: React.FC<Props> = ({onCallback}) => {
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [currentPlan, setCurrentPlan] = useState<ZuoraCurrentPlan>()

  const renewalAvailable = false

  const handleOpenIntercom = () => {
    intercom.addCustomMessage("")
  }

  useEffect(() => {
    const loaded = buService.getCurrentPlan({businessId: authentication.business().uuid!}).then((data: ZuoraCurrentPlan) => {
      setCurrentPlan(data)
    })

    setLoad(loaded)
  }, [])

  return (
    <Loading load={load}>
      {renewalAvailable && (
        <BillingCard padding={[3, 2]} justifyContent="space-between" marginBottom={4}>
          <Flex alignItems="center">
            <Icon icon={IconTypes.info} color={mainTheme.colors.tertiary.o_400} size={24} />
            <Body marginLeft color={mainTheme.colors.filterChip}>
              <FormattedMessage id="admin.billing.currentPlan.renewal.title" />
            </Body>
          </Flex>
          <Button onClick={onCallback}>
            <FormattedMessage id="admin.billing.currentPlan.renewal.btn" />
          </Button>
        </BillingCard>
      )}
      <BillingCard padding={4} direction="column">
        <Flex justifyContent="space-between" width="100%" marginBottom={2}>
          <H2>
            <FormattedMessage id="admin.billing.currentPlan.title" />
          </H2>
          {renewalAvailable && (
            <BillingRenewal alignSelf="center" padding={1}>
              <Body small color={mainTheme.colors.utility.white}>
                <FormattedMessage id="admin.billing.currentPlan.renewalAvailable" />
              </Body>
            </BillingRenewal>
          )}
        </Flex>
        <Flex gap={mainTheme.spacing.one} marginBottom={4}>
          <BillingChip id="adminBilling_currentPlan_seats" alignItems="center">
            <Icon icon={IconTypes.userPlus} size={18} />
            <Body small color={mainTheme.colors.utility.white} marginLeft>
              <FormattedMessage id="admin.billing.currentPlan.seats" values={{value: currentPlan?.seats}} />
            </Body>
          </BillingChip>
          {currentPlan?.addOns?.map((product: ZuoraProduct) => {
            const isFlexiPoints = product.name?.includes("Flexi points")
            return (
              <BillingChip id={`adminBilling_currentPlan_${product.name}`} alignItems="center">
                <Icon
                  color={isFlexiPoints ? mainTheme.colors.utility.white : null}
                  icon={isFlexiPoints ? IconTypes.flexi : IconTypes.multiComments}
                  size={18} />
                <Body small color={mainTheme.colors.utility.white} marginLeft>
                  {product.name}
                </Body>
              </BillingChip>
            )
          })}
        </Flex>
        <div>
          <BillingInfo padding={mainTheme.spacing.oneAndHalf}>
            <Body>
              <FormattedMessage id="admin.billing.currentPlan.startDate" />
            </Body>
            {currentPlan?.startDate && <H4 weight="bold">{moment(currentPlan.startDate).format("DD MMM YYYY")}</H4>}
          </BillingInfo>
          <BillingInfo padding={mainTheme.spacing.oneAndHalf}>
            <Body>
              <FormattedMessage id="admin.billing.currentPlan.renewalDate" />
            </Body>
            {currentPlan?.endDate && <H4 weight="bold">{moment(currentPlan.endDate).format("DD MMM YYYY")}</H4>}
          </BillingInfo>
        </div>
        <Flex gap={mainTheme.spacing.oneAndHalf} marginTop={4}>
          <Button onClick={handleOpenIntercom}>
            <FormattedMessage id="admin.billing.currentPlan.renewal.changePlanBtn" />
          </Button>
          {renewalAvailable && (
            <Button primary onClick={onCallback}>
              <FormattedMessage id="admin.billing.currentPlan.renewal.btn" />
            </Button>
          )}
        </Flex>
      </BillingCard>
    </Loading>
  )
}

export default CurrentPlan
